import React, { useEffect, useState } from "react";
import {
    Modal,
    ModalHeader,
    ModalBody,
    ModalFooter,
    Button,
    Row,
    Col,
    Input,
    Label,
} from "reactstrap";

import { Network, Urls } from "../../apiConfiguration";
import { showErrorMessage } from "../../components";
import { useLoader } from "../../hooks";

const FormModal = ({ show, setShow, setCheck, initialData }) => {
    const { setLoading } = useLoader();

    const [name, setName] = useState("");
    const [weeklyHours, setWeeklyHours] = useState("");
    const [monthlyHours, setMonthlyHours] = useState({}); // { January: 160, ... }

    useEffect(() => {
        if (!show || !initialData) return;

        // basic fields
        setName(initialData?.name || "");
        setWeeklyHours(
            initialData?.weekly_hours !== undefined && initialData?.weekly_hours !== null
                ? String(initialData.weekly_hours)
                : ""
        );

        // map months array -> { month_name: monthly_hours }
        const hours = {};
        (initialData.months || []).forEach((m) => {
            hours[m.month_name] = m.monthly_hours;
        });
        setMonthlyHours(hours);
    }, [show, initialData]);

    const validate = () => {
        if (!name.trim()) return "Office location name is required.";

        const n = Number(weeklyHours);
        if (weeklyHours === "" || Number.isNaN(n)) return "Weekly hours must be a number.";
        if (n < 0) return "Weekly hours cannot be negative.";
        if (n > 60) return "Weekly hours cannot be more than 60.";

        // Optional: basic validation for monthly hours
        for (const [month, val] of Object.entries(monthlyHours)) {
            const num = Number(val);
            if (val === "" || Number.isNaN(num)) {
                return `Monthly hours for ${month} must be a number.`;
            }
            if (num < 0) {
                return `Monthly hours for ${month} cannot be negative.`;
            }
        }

        return null;
    };

    const submit = async () => {
        const err = validate();
        if (err) return showErrorMessage(err);

        // convert back to array of months for API
        const monthsPayload = (initialData?.months || []).map((m) => ({
            id: m.id,
            office_location_id: m.office_location_id,
            month_name: m.month_name,
            monthly_hours: Number(
                monthlyHours[m.month_name] !== undefined
                    ? monthlyHours[m.month_name]
                    : m.monthly_hours
            ),
        }));

        const payload = {
            name: name.trim(),
            weekly_hours: Number(weeklyHours),
            months: monthsPayload,
        };

        setLoading(true);

        // edit-only: always update existing office location
        const url = Urls.updateOfficeLocation(initialData?.id);
        const response = await Network.post(url, payload);

        setLoading(false);

        if (!response.ok) return showErrorMessage(response.data.message);

        setCheck((p) => !p);
        setShow(false);
    };

    return (
        <Modal isOpen={show} toggle={() => setShow(false)} size="lg">
            <ModalHeader toggle={() => setShow(false)}>
                Edit office location
            </ModalHeader>

            <ModalBody>
                <Row className="mb-3">
                    <Col md={6}>
                        <Label className="mb-1">Name</Label>
                        <Input
                            value={name}
                            onChange={(e) => setName(e.target.value)}
                            placeholder="e.g. Canada"
                        />
                    </Col>

                    <Col md={6}>
                        <Label className="mb-1">Weekly hours</Label>
                        <Input
                            type="number"
                            min="0"
                            max="168"
                            step="0.5"
                            value={weeklyHours}
                            onChange={(e) => setWeeklyHours(e.target.value)}
                            placeholder="e.g. 40"
                        />
                    </Col>
                </Row>

                {initialData?.months && initialData.months.length > 0 && (
                    <>
                        <hr className="my-3" />
                        <Row>
                            <Col md={12}>
                                <Label className="mb-2">Monthly hours</Label>
                            </Col>

                            {initialData.months.map((m) => (
                                <Col md={3} className="mb-2" key={m.id}>
                                    <Label className="mb-1">{m.month_name}</Label>
                                    <Input
                                        type="number"
                                        min="0"
                                        step="0.5"
                                        value={monthlyHours[m.month_name] ?? ""}
                                        onChange={(e) =>
                                            setMonthlyHours((prev) => ({
                                                ...prev,
                                                [m.month_name]: e.target.value,
                                            }))
                                        }
                                    />
                                </Col>
                            ))}
                        </Row>
                    </>
                )}
            </ModalBody>

            <ModalFooter>
                <Button color="secondary" onClick={() => setShow(false)}>
                    Cancel
                </Button>
                <Button color="primary" onClick={submit}>
                    Update
                </Button>
            </ModalFooter>
        </Modal>
    );
};

export default FormModal;
